<template>
  <!-- Single watermark pattern canvas (hidden) -->
  <canvas ref="patternCanvas" id="water-mark-pattern"></canvas>

  <!-- Full watermark area -->
  <canvas ref="watermarkCanvas" id="water-mark"></canvas>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";

const patternCanvas = ref<HTMLCanvasElement | null>(null);
const watermarkCanvas = ref<HTMLCanvasElement | null>(null);

let resizeHandler: (() => void) | null = null;

/**
 * Draw single watermark pattern
 */
const drawPattern = (ctx: CanvasRenderingContext2D) => {
  // Single watermark dimensions
  const width = 120;
  const height = 80;
  const text = "OxValue.AI Demo";

  // Clear rectangular area for watermark content
  ctx.clearRect(0, 0, width, height);

  // Set font size
  ctx.font = "bold 30px Microsoft Yahei";

  // Set watermark color
  ctx.fillStyle = "rgba(0, 0, 0, 0.082)";

  // Rotate by angle
  ctx.rotate((-Math.PI / 180) * 19);

  // Set content and coordinates
  ctx.fillText(text, -10, 120);
};

/**
 * Tile single watermark across entire page
 */
const drawWaterMark = (ctx: CanvasRenderingContext2D) => {
  if (!watermarkCanvas.value || !patternCanvas.value) return;

  // Get container dimensions - prefer parent container, fallback to body
  const container = watermarkCanvas.value.parentElement;
  const clientWidth = container?.clientWidth || document.body.clientWidth;
  const clientHeight = container?.clientHeight || document.body.clientHeight;

  watermarkCanvas.value.width = clientWidth;
  watermarkCanvas.value.height = clientHeight;

  // Clear content in area before drawing
  ctx.clearRect(0, 0, clientWidth, clientHeight);

  // createPattern: repeat specified element in specified direction
  // Here it tiles our single watermark pattern across entire screen
  const pattern = ctx.createPattern(patternCanvas.value, "repeat");

  if (pattern) {
    ctx.fillStyle = pattern;
    ctx.fillRect(0, 0, clientWidth, clientHeight);
  }
};

/**
 * Initialize watermark
 */
const init = () => {
  if (!patternCanvas.value || !watermarkCanvas.value) return;

  const patternCtx = patternCanvas.value.getContext("2d");
  const waterMarkCtx = watermarkCanvas.value.getContext("2d");

  if (!patternCtx || !waterMarkCtx) return;

  // Draw pattern
  drawPattern(patternCtx);

  // Draw watermark
  drawWaterMark(waterMarkCtx);

  // Hide pattern canvas (but keep it in DOM for html2canvas)
  if (patternCanvas.value) {
    patternCanvas.value.style.position = "absolute";
    patternCanvas.value.style.left = "-9999px";
    patternCanvas.value.style.visibility = "hidden";
  }

  // Setup resize listener
  resizeHandler = () => {
    if (waterMarkCtx) {
      drawWaterMark(waterMarkCtx);
    }
  };

  window.addEventListener("resize", resizeHandler);
};

onMounted(() => {
  init();
});

onUnmounted(() => {
  if (resizeHandler) {
    window.removeEventListener("resize", resizeHandler);
  }
});
</script>

<style scoped>
#water-mark {
  position: absolute;
  z-index: 9999;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  pointer-events: none; /* Prevent watermark from blocking user interaction */
  /* display: none; - Removed to allow html2canvas to capture watermark */
}
</style>
